import { useMemo } from 'react'
import Notifications from '@mui/icons-material/Notifications'
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined'
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetMyNotifications,
  useGetUnreadNotificationsCount,
  useMarkAllNotificationsRead,
  useSetNotificationReadState,
} from '@/service-library/hooks/notifications'
import useDocumentSet from '@/services/hooks/useDocumentSet'
import NotificationRow from './NotificationRow'
import useNotificationPopups from './useNotificationPopups'

function EmptyState() {
  return (
    <Stack alignItems="center" spacing={1} sx={{ px: 4, py: 5 }}>
      <Box
        sx={{
          display: 'flex',
          p: 1,
          borderRadius: '50%',
          bgcolor: 'action.selected',
          color: 'text.secondary',
        }}
      >
        <NotificationsOutlined />
      </Box>
      <Typography variant="body1">No notifications yet</Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        Approval requests and mentions will show up here.
      </Typography>
    </Stack>
  )
}

export default function NotificationsMenu() {
  const overlay = useOverlay()
  const { unreadCount } = useGetUnreadNotificationsCount()
  useNotificationPopups({ isPanelOpen: overlay.isOpen })

  const {
    notifications,
    isPending,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetMyNotifications({
    enabled: overlay.isOpen,
    // Same pace as the badge, so a new notification shows up in the open list
    // about when the unread count goes up. Stops while the popover is closed.
    refetchInterval: 60_000,
  })

  const documentIds = useMemo(
    () => [
      ...new Set(
        notifications.flatMap(({ link_obj_id }) =>
          link_obj_id ? [link_obj_id] : [],
        ),
      ),
    ],
    [notifications],
  )
  // Splits the ids across requests once enough pages are loaded to make
  // one URL too long. Its own enabled check is replaced by ours, so ours
  // includes the empty case.
  const { documents, isPending: isPendingDocuments } = useDocumentSet({
    documentIds,
    fieldsOnly: 'id,name',
    enabled: overlay.isOpen && documentIds.length > 0,
  })
  const documentNames = useMemo(
    () =>
      Object.fromEntries((documents ?? []).map(({ id, name }) => [id, name])),
    [documents],
  )

  const { setNotificationReadState } = useSetNotificationReadState()
  const { markAllNotificationsRead } = useMarkAllNotificationsRead()

  let content = <EmptyState />
  if (isPending) {
    content = (
      <Stack alignItems="center" sx={{ p: 4 }}>
        <CircularProgress size={24} />
      </Stack>
    )
  } else if (isError) {
    content = (
      <Typography color="error" sx={{ p: 3 }} align="center">
        Couldn&apos;t load notifications.
      </Typography>
    )
  } else if (notifications.length) {
    content = (
      <List disablePadding>
        {notifications.map((notification) => (
          <NotificationRow
            key={notification.id}
            notification={notification}
            documentName={
              notification.link_obj_id
                ? documentNames[notification.link_obj_id]
                : undefined
            }
            documentLoading={isPendingDocuments}
            onOpen={overlay.close}
            onSetRead={(read) => {
              setNotificationReadState({ id: notification.id, read })
            }}
          />
        ))}
      </List>
    )
  }

  return (
    <div>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={
            unreadCount
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications'
          }
          onClick={overlay.toggle}
          size="small"
          sx={{ bgcolor: overlay.isOpen ? 'action.selected' : undefined }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <Notifications />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={overlay.isOpen}
        anchorEl={overlay.anchorEl as Element}
        onClose={overlay.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxWidth: 'calc(100vw - 32px)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'min(600px, calc(100vh - 80px))',
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1">Notifications</Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} unread`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
          <Button
            variant="text"
            sx={{ textTransform: 'none' }}
            disabled={unreadCount === 0}
            onClick={() => markAllNotificationsRead()}
          >
            Mark all as read
          </Button>
        </Stack>
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>{content}</Box>
        {hasNextPage && (
          <Stack sx={{ borderTop: 1, borderColor: 'divider', py: 0.5 }}>
            <Button
              variant="text"
              sx={{ textTransform: 'none' }}
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </Stack>
        )}
      </Popover>
    </div>
  )
}
